import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SectionSurface from '../shared/SectionSurface';
import DodFlow from './DodFlow';
import {
  DELIVERY_HERO,
  DELIVERY_DOD_HEADER,
  DELIVERY_ACCOUNT,
  DELIVERY_VISION,
  DELIVERY_STRATEGIES,
  DELIVERY_GUIDING_PRINCIPLES,
  DELIVERY_PRINCIPLES_INTRO,
  DELIVERY_AGILE_LOOP,
  DELIVERY_AGILE_OUTCOME,
  DELIVERY_VALUE_GROWTH,
  DELIVERY_SPRINT_FLOW,
  DELIVERY_SYSTEM_OF_RECORD,
  DELIVERY_TRADEMARK,
} from '../../../content/enterpriseMessaging';
import { DELIVERY_SAMPLE_BOARD } from '../../../content/deliveryPageCopy';

function FlowChevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="mx-auto my-1 h-5 w-5 shrink-0 rotate-90 lg:mx-1 lg:my-0 lg:rotate-0"
      aria-hidden="true"
    >
      <path d="M4 12h14" fill="none" stroke="#0091CC" strokeWidth="2.25" strokeLinecap="round" />
      <path d="M13 6l6 6-6 6" fill="none" stroke="#0091CC" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BoardCard({ card, type, flow }) {
  const active = Boolean(card.active);
  return (
    <div
      aria-hidden={active ? undefined : 'true'}
      className={`mb-2 rounded-md border border-l-4 p-2 shadow-sm ${type.className} ${
        active ? 'border-brand-600 ring-2 ring-brand-700' : 'border-ink-200 opacity-80'
      }`}
    >
      <div className="flex items-center justify-between gap-2 text-xs font-semibold text-ink-600">
        <span>{card.id}</span>
        <span
          className={`rounded-full px-2 py-0.5 ${active ? 'bg-brand-700 text-white' : 'bg-white text-ink-600'}`}
        >
          {active ? flow.sample.label : flow.ghostTag}
        </span>
      </div>
      <p className="mt-1 text-sm leading-snug text-ink-900">{card.title}</p>
      {active ? (
        <>
          <p className="mt-1 text-xs leading-snug text-ink-700">{flow.sample.text}</p>
          <p className="mt-1 text-xs font-medium leading-snug text-brand-700">{flow.sample.detail}</p>
        </>
      ) : null}
      <div className="mt-2 flex items-center justify-between text-xs text-ink-600">
        <span>{type.label}</span>
        <span>
          <span className="rounded-full bg-white px-2 py-0.5 font-medium">{card.owner}</span> {card.points} pts
        </span>
      </div>
    </div>
  );
}

function MobileBoardList({ flow }) {
  const data = DELIVERY_SAMPLE_BOARD;
  const typeByKey = Object.fromEntries(data.types.map((t) => [t.key, t]));
  return (
    <ol className="mt-4 space-y-3 sm:hidden" aria-label={flow.ariaLabel.replace(', scrolls horizontally', '')} data-testid="sample-board-mobile">
      {flow.board.map(({ state, label }) => {
        const cards = data.cards.filter((c) => c.state === state);
        return (
          <li key={state} className="rounded-lg bg-surface-muted p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-logo-dark-blue">
              {label} <span className="font-medium normal-case text-ink-500">({cards.length})</span>
            </p>
            <div className="mt-2">
              {cards.map((card) => (
                <BoardCard key={card.id} card={card} type={typeByKey[card.type]} flow={flow} />
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function SampleBoard({ flow }) {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const update = () => {
      setEdges({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
    };
  }, []);

  const data = DELIVERY_SAMPLE_BOARD;
  const typeByKey = Object.fromEntries(data.types.map((t) => [t.key, t]));
  const columns = flow.board;
  const countFor = (state) => data.cards.filter((c) => c.state === state).length;

  return (
    <div className="relative mt-4">
      <div
        ref={scrollerRef}
        tabIndex={0}
        role="region"
        aria-label={flow.ariaLabel}
        className="hidden sm:block overflow-x-auto rounded-md pb-3 outline-2 outline-transparent focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
      >
        <p className="sr-only">{flow.srSummary}</p>
        <table className="w-max border-separate border-spacing-1 text-left text-sm" data-testid="sample-board">
          <caption className="sr-only">{data.title}</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 min-w-[160px] rounded-md bg-logo-dark-blue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white">
                {data.rowHeader}
              </th>
              {columns.map(({ state, label }) => (
                <th key={state} scope="col" className="min-w-[160px] rounded-md bg-logo-dark-blue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white">
                  {label}
                  <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium normal-case">{countFor(state)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.key}>
                <th scope="row" className="sticky left-0 z-10 rounded-md bg-surface-muted px-3 py-3 align-top">
                  <span className="block text-sm font-semibold text-ink-900">{row.label}</span>
                  <span className="mt-1 block text-xs text-ink-500">{row.goal}</span>
                </th>
                {columns.map(({ state }) => {
                  const cards = data.cards.filter((c) => c.row === row.key && c.state === state);
                  return (
                    <td key={state} className="w-[190px] min-w-[160px] max-w-[200px] whitespace-normal break-words rounded-md bg-surface-muted/60 p-2 align-top">
                      {cards.map((card) => {
                        const type = typeByKey[card.type];
                        return (
                          <BoardCard key={card.id} card={card} type={type} flow={flow} />
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edges.left ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-linear-to-r from-white to-transparent"
        />
      ) : null}
      {edges.right ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-white to-transparent"
        />
      ) : null}
      <MobileBoardList flow={flow} />
      <ul className="mt-3 flex flex-wrap items-center gap-4 text-xs text-ink-600" aria-label="Board legend">
        <li className="font-semibold uppercase tracking-wide text-ink-700">Legend</li>
        {data.types.map((t) => (
          <li key={t.key} className="flex items-center gap-2">
            <span aria-hidden="true" className={`inline-block h-3 w-5 rounded-sm border border-ink-200 border-l-4 ${t.className}`} />
            {t.label}
          </li>
        ))}
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="inline-block h-3 w-5 rounded-sm ring-2 ring-brand-700" />
          {flow.sample.label}
        </li>
        <li>Initials = owner</li>
        <li>pts = story points</li>
        <li>(n) = items in stage</li>
      </ul>
    </div>
  );
}

function SprintReleaseFlow({ flow }) {
  return (
    <div>
      <div id="sample-board" className="min-w-0 rounded-xl border border-ink-200 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-ink-900">{flow.boardHeading}</p>
            <p className="mt-1 text-xs font-medium text-ink-500">{DELIVERY_SAMPLE_BOARD.title}</p>
            <p className="mt-1 text-sm text-ink-600">{DELIVERY_SAMPLE_BOARD.caption}</p>
          </div>
          <p className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">{DELIVERY_SAMPLE_BOARD.badge}</p>
        </div>
        <SampleBoard flow={flow} />
      </div>
      <ol aria-label="Delivery stages" className="mt-6 flex flex-col lg:flex-row lg:items-stretch">
        {flow.steps.map((step, i) => (
          <li key={step.title} className="flex min-w-0 flex-1 flex-col lg:flex-row lg:items-center">
            <div className="min-w-0 flex-1 rounded-xl border border-ink-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-logo-dark-blue text-xs font-semibold text-white">
                  {i + 1}
                </span>
                <h3 className="text-base font-semibold text-ink-900">{step.title}</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-ink-600">{step.body}</p>
            </div>
            {i < flow.steps.length - 1 ? <FlowChevron /> : null}
          </li>
        ))}
      </ol>

    </div>
  );
}

export default function DeliveryDetails() {
  const sor = DELIVERY_SYSTEM_OF_RECORD;

  return (
    <>
      <SectionSurface variant="wash" as="section" decor="none" underNav bleed="bottom" flowBottom overlap>
        <div className="container-x section">
          <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">{DELIVERY_HERO.eyebrow}</p>
            <h1 className="mt-3 text-fluid-h1 font-semibold tracking-tight text-ink-900">
              {DELIVERY_HERO.title}
            </h1>
            <p className="mt-5 text-lg text-ink-600 leading-relaxed">{DELIVERY_HERO.subtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={DELIVERY_HERO.primaryCta.href} className="btn-primary btn-lg">
                {DELIVERY_HERO.primaryCta.label}
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a href={DELIVERY_HERO.secondaryCta.href} className="btn-secondary btn-lg">
                {DELIVERY_HERO.secondaryCta.label}
              </a>
            </div>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="elevated" as="section" id="delivery-principles" decor="none" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">{DELIVERY_PRINCIPLES_INTRO.eyebrow}</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              {DELIVERY_PRINCIPLES_INTRO.title}
            </h2>
            <p className="mt-4 text-lg text-ink-600 leading-relaxed">{DELIVERY_PRINCIPLES_INTRO.intro}</p>
          </div>
          <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {DELIVERY_GUIDING_PRINCIPLES.map((p, i) => (
              <li key={p} className="rounded-xl border-t-4 border-brand-700 bg-white p-6 shadow-sm">
                <span className="text-xs font-bold tracking-widest text-brand-600">{String(i + 1).padStart(2, '0')}</span>
                <p className="mt-2 text-lg font-semibold leading-snug text-ink-900">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </SectionSurface>

      <SectionSurface variant="muted" as="section" id="customer-account" decor="contour" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">{DELIVERY_ACCOUNT.eyebrow}</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              {DELIVERY_ACCOUNT.title}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">{DELIVERY_ACCOUNT.body}</p>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="elevated" as="section" id="vision" decor="flow" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">Vision & strategies</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              {DELIVERY_VISION.title}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">{DELIVERY_VISION.body}</p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {DELIVERY_STRATEGIES.map((s) => (
              <li key={s} className="border-t-2 border-brand-700 bg-white px-5 py-4 text-ink-700 leading-relaxed">
                {s}
              </li>
            ))}
          </ul>
        </div>
      </SectionSurface>

      <SectionSurface variant="muted" as="section" id="agile" decor="wave" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">Iteration model</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              How each request moves
            </h2>
            <p className="mt-4 text-lg text-ink-600 leading-relaxed">
              Every request follows the same five steps, from first conversation to working results you review.
            </p>
          </div>
          <div aria-hidden="true" className="mt-10 hidden grid-cols-5 gap-8 lg:grid">
            <p className="col-span-2 text-xs font-semibold uppercase tracking-[0.08em] text-ink-600">Planning 1–2</p>
            <p className="col-span-3 text-xs font-semibold uppercase tracking-[0.08em] text-ink-600">Execution 3–5</p>
          </div>
          <ol className="mt-10 grid grid-cols-1 gap-8 lg:mt-3 lg:grid-cols-5" data-testid="iteration-steps">
            {DELIVERY_AGILE_LOOP.map((s, i) => (
              <li key={s.step} className="min-w-0" data-testid="iteration-step">
                {i === 0 || i === 2 ? (
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-ink-600 lg:hidden">
                    {i === 0 ? 'Planning 1–2' : 'Execution 3–5'}
                  </p>
                ) : null}
                <article className="card h-full p-5">
                  <span className="eyebrow text-brand-900">
                    <span className="sr-only">{i < 2 ? 'Planning, step ' : 'Execution, step '}</span>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-2 text-base font-semibold leading-snug text-ink-900">{s.step}</h3>
                  <p className="mt-2 text-sm leading-6 text-ink-600">{s.body}</p>
                </article>
              </li>
            ))}
          </ol>
          <p className="mt-8 border-l-4 border-brand-700 bg-brand-50 px-4 py-3 text-ink-700">{DELIVERY_AGILE_OUTCOME}</p>
          <div className="mt-10 max-w-2xl">
            <h3 className="text-lg font-semibold text-ink-900">Value grows across iterations</h3>
            <ul className="mt-4 space-y-3">
              {DELIVERY_VALUE_GROWTH.map((v) => (
                <li key={v.label} className="border-t border-ink-200 pt-3 text-ink-600">
                  <strong className="text-ink-900">{v.label}.</strong> {v.body}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-ink-500">
              Each iteration converts feedback into the next prioritized backlog.
            </p>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="elevated" as="section" id="sprint-to-release" decor="nodes" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">{DELIVERY_SPRINT_FLOW.eyebrow}</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              {DELIVERY_SPRINT_FLOW.title}
            </h2>
            <p className="mt-4 border-l-4 border-brand-700 bg-brand-50 px-4 py-3 text-sm leading-relaxed text-ink-700">
              {DELIVERY_SPRINT_FLOW.note}
            </p>
          </div>
          <div className="mt-10">
            <SprintReleaseFlow flow={DELIVERY_SPRINT_FLOW} />
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="elevated" as="section" id="definition-of-done" aria-labelledby="dod-title" decor="dots" bleed="both" flowTop flowBottom overlap className="py-fluid-section">
        <div className="mx-auto w-full min-w-0 max-w-[calc(var(--content-max)+2*var(--space-gutter))] px-fluid-gutter">
          <div className="max-w-measure">
            <p className="eyebrow !text-fluid-eyebrow !text-brand-700">{DELIVERY_DOD_HEADER.eyebrow}</p>
            <h2 id="dod-title" className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">
              Definition of Done
            </h2>
            <p className="mt-fluid-stack max-w-measure text-fluid-body text-ink-600">{DELIVERY_DOD_HEADER.intro}</p>
          </div>
          <DodFlow />
        </div>
      </SectionSurface>

      <SectionSurface variant="muted" as="section" id="system-of-record" decor="rings" bleed="top" flowTop overlap className="section">
        <div className="container-x">
          <div className="max-w-3xl">
            <p className="eyebrow">Tooling</p>
            <h2 className="mt-3 text-fluid-h2 font-semibold tracking-tight text-ink-900">{sor.title}</h2>
            <p className="mt-4 text-lg text-ink-600 leading-relaxed">{sor.lead}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {sor.pillars.map((p) => (
              <article key={p.title} className="rounded-xl border border-ink-200 bg-white p-6">
                <h3 className="text-lg font-semibold text-ink-900">{p.title}</h3>
                <p className="mt-3 text-ink-600 leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
          <ul className="mt-8 space-y-3">
            {sor.boards.map((b) => (
              <li key={b.name} className="border-t border-ink-200 pt-3 text-ink-600">
                <strong className="text-ink-900">{b.name}.</strong> {b.body}
              </li>
            ))}
          </ul>
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2" id="pipelines">
            <article className="rounded-xl border border-ink-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-ink-900">{sor.pipelines.title}</h3>
              <p className="mt-3 text-ink-600 leading-relaxed">{sor.pipelines.body}</p>
            </article>
            <article className="rounded-xl border border-ink-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-ink-900">{sor.supporting.title}</h3>
              <p className="mt-3 text-ink-600 leading-relaxed">{sor.supporting.body}</p>
            </article>
          </div>
          <div className="mt-10">
            <a href={DELIVERY_HERO.primaryCta.href} className="btn-primary btn-lg">
              {DELIVERY_HERO.primaryCta.label}
              <ArrowRightIcon className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-8 max-w-3xl text-xs leading-relaxed text-ink-600">{DELIVERY_TRADEMARK}</p>
        </div>
      </SectionSurface>
    </>
  );
}
